import { median } from '../vocal/statistics.js'

/**
 * Input-latency calibration - pure.
 *
 * Every mic/worklet and MIDI path carries 20-100 ms of delay between the note a musician plays and
 * the number this code sees. That delay has to be measured, because the tempting alternative -
 * estimating it as the mean of a performance's onset errors - destroys the one thing this tool
 * exists to report: rushing and dragging ARE the mean of the onset errors, so subtracting the mean
 * would make every player perfectly even and the feedback a lie in exactly its own dimension.
 *
 * So: eight clicks, the user taps along, and the median of the last six is the answer.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.3
 */

/** Eight clicks. Long enough to settle into, short enough to redo without irritation. */
export const latencyClickCount = 8

/**
 * ...of which the last six are scored.
 *
 * The first two are the user finding the pulse. A tap test that counted them would be measuring
 * attention rather than the device, and the first tap of a run is reliably the worst one.
 * @see spec §6.3
 */
export const latencyScoredTaps = 6

export interface LatencyCalibration {
  /** Median ms between a click and the tap it drew. Signed - see below. */
  inputLatencyMs: number
  /** The samples the median was taken over, so a panel can show the spread it came from. */
  samples: number[]
}

/**
 * The distance from a tap to the click it was aiming at.
 *
 * NEAREST click, never the click of the same index. A single missed tap otherwise shifts every
 * later tap onto the click after the one it answered, and the median moves by a whole beat - the
 * difference between a 70 ms answer and a 670 ms one, which would then be subtracted from every
 * onset the player produced for as long as that device is in use.
 */
const nearestClickError = (clickTimesMs: number[], tapMs: number): number =>
  clickTimesMs.reduce((closest, click) => (Math.abs(tapMs - click) < Math.abs(closest) ? tapMs - click : closest), Infinity)

export const latencyFromTaps = (clickTimesMs: number[], tapTimesMs: number[]): LatencyCalibration | null => {
  if (clickTimesMs.length === 0) {
    return null
  }

  const errors = tapTimesMs.map(tap => nearestClickError(clickTimesMs, tap))

  if (errors.length < latencyScoredTaps) {
    /**
     * Not enough taps to answer with. §6.3 names eight clicks and six scored taps and says nothing
     * about the failure case; refusing is the only safe direction, because the absence of a
     * calibration is a state the tool already handles - it withholds the timing score - while a
     * calibration built from three taps would be silently wrong for every take afterwards.
     */
    return null
  }

  const samples = errors.slice(-latencyScoredTaps)

  /*
   * Median, per §6.3, and not clamped to zero. A player who consistently anticipates the click
   * genuinely measures negative; clamping would turn a bad calibration into a plausible-looking
   * one, and the panel showing the spread is what lets a person notice.
   */
  return { inputLatencyMs: median(samples), samples }
}
